/**
 * Request Manager - Prevents duplicate API calls
 * Uses axios CancelToken to cancel previous requests when new ones are made
 */

import axios from 'axios';

class RequestManager {
  constructor() {
    this.pendingRequests = new Map();
  }

  /**
   * Generate a unique key for a request based on method and URL
   * @param {Object} config - Axios request config
   * @returns {String} Request key
   */
  generateRequestKey(config) {
    return `${config.method || 'get'}:${config.url}`;
  }

  /**
   * Cancel a specific request if it exists
   * @param {String} key - Request key
   */
  cancelRequest(key) {
    const pendingRequest = this.pendingRequests.get(key);
    if (pendingRequest && pendingRequest.cancel) {
      pendingRequest.cancel('Request cancelled due to duplicate request');
      this.pendingRequests.delete(key);
    }
  }

  /**
   * Create a new cancel token for a request
   * @param {String} key - Request key
   * @returns {Object} Cancel token
   */
  createCancelToken(key) {
    // Cancel previous request with same key if it exists
    this.cancelRequest(key);
    
    // Create new cancel token for axios 0.18.0
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    
    // Store the cancel function with the request key
    this.pendingRequests.set(key, { 
      cancel: source.cancel, 
      timestamp: Date.now() 
    });
    
    return source.token;
  }

  /**
   * Remove a request from pending requests
   * @param {String} key - Request key
   */
  removeRequest(key) {
    this.pendingRequests.delete(key);
  }

  /**
   * Cleanup old pending requests (older than 5 minutes)
   */
  cleanupOldRequests() {
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
    for (const [key, value] of this.pendingRequests.entries()) {
      if (value.timestamp < fiveMinutesAgo) {
        this.pendingRequests.delete(key);
      }
    }
  }

  /**
   * Get all pending requests
   * @returns {Map} Map of pending requests
   */
  getPendingRequests() {
    return this.pendingRequests;
  }
}

export default new RequestManager();

